import {useQuery} from '@tanstack/react-query';
import React, {createContext, useContext, ReactNode, useState} from 'react';
import QUERY_KEYS from '../../constants/queryKeys';
import {handleGetAllRoleTypesService} from '../../services/roleTypes';

type DefaultSelectedNode = {department: string; role: string; shift: string};

export type OnBoardingDraftSaveStatus =
  | 'idle'
  | 'saving'
  | 'saved'
  | 'failed';

interface OnBoardingContextType {
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  showSetupNetworkScreen: boolean;
  setShowSetupNetworkScreen: React.Dispatch<React.SetStateAction<boolean>>;
  defaultSelectedNodeByPath: Record<string, DefaultSelectedNode>;
  getDefaultSelectedNode: (fieldPath: string) => DefaultSelectedNode;
  setDefaultSelectedNodeForPath: (
    fieldPath: string,
    value: React.SetStateAction<DefaultSelectedNode>,
  ) => void;
  draftSaveStatus: OnBoardingDraftSaveStatus;
  setDraftSaveStatus: React.Dispatch<
    React.SetStateAction<OnBoardingDraftSaveStatus>
  >;
  draftLastSavedAt: Date | null;
  setDraftLastSavedAt: React.Dispatch<React.SetStateAction<Date | null>>;
  allRoleTypes: {
    data?: Array<{
      _id: string;
      name?: {he?: string; en?: string};
      icon?: string;
    }>;
  };
}

const OnBoardingContext = createContext<OnBoardingContextType | null>(null);

interface OnBoardingProviderProps {
  children: ReactNode;
}

export const OnBoardingContextProvider = ({
  children,
}: OnBoardingProviderProps) => {
  const [currentStep, setCurrentStep] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('step') === '2' ? 2 : 1;
  });
  const [showSetupNetworkScreen, setShowSetupNetworkScreen] = useState(false);
  const [draftSaveStatus, setDraftSaveStatus] =
    useState<OnBoardingDraftSaveStatus>('idle');
  const [draftLastSavedAt, setDraftLastSavedAt] = useState<Date | null>(null);
  const [defaultSelectedNodeByPath, setDefaultSelectedNodeByPath] = useState<
    Record<string, DefaultSelectedNode>
  >({});

  const {data: allRoleTypes} = useQuery({
    queryKey: [QUERY_KEYS.GET_ALL_ROLE_TYPES],
    queryFn: handleGetAllRoleTypesService,
  });

  const getDefaultSelectedNode = (fieldPath: string): DefaultSelectedNode =>
    defaultSelectedNodeByPath[fieldPath] ?? {
      department: 'template',
      role: 'template',
      shift: 'template',
    };

  const setDefaultSelectedNodeForPath = (
    fieldPath: string,
    value: React.SetStateAction<DefaultSelectedNode>,
  ) => {
    setDefaultSelectedNodeByPath(prev => {
      const currentValue = prev[fieldPath] ?? {
        department: 'template',
        role: 'template',
        shift: 'template',
      };
      const nextValue =
        typeof value === 'function' ? value(currentValue) : value;

      return {
        ...prev,
        [fieldPath]: nextValue,
      };
    });
  };

  return (
    <OnBoardingContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        showSetupNetworkScreen,
        setShowSetupNetworkScreen,
        defaultSelectedNodeByPath,
        getDefaultSelectedNode,
        setDefaultSelectedNodeForPath,
        draftSaveStatus,
        setDraftSaveStatus,
        draftLastSavedAt,
        setDraftLastSavedAt,
        allRoleTypes,
      }}>
      {children}
    </OnBoardingContext.Provider>
  );
};

export const useOnBoardingContext = () => {
  const context = useContext(OnBoardingContext);
  if (!context) {
    throw new Error(
      'useOnBoardingContext must be used within an OnBoardingContextProvider',
    );
  }
  return context;
};
